import { useEffect, useState } from 'react';
import { ExternalLink, Heart, Loader2, PartyPopper, Share2, Star, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { nextEventContactPath } from '../../utils/endOfShow';

interface ShowWrapUpModalProps {
  eventId: string;
  eventName: string;
  showFeedback: boolean;
  initialStep?: WrapUpStep;
  onSubmitFeedback: (rating: number, message: string) => Promise<void>;
  onShare: () => void;
  onClose: () => void;
}

export type WrapUpStep = 'thanks' | 'feedback' | 'share';

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent!'];

// Shown once per event to each attendee when the night ends: a Last Song stop (ITER-175), End Event, the Settings
// Completed button, Last Song auto-end or the end time passing (ITER-199). thanks → feedback → share
export function ShowWrapUpModal({
  eventId,
  eventName,
  showFeedback,
  initialStep = 'thanks',
  onSubmitFeedback,
  onShare,
  onClose,
}: ShowWrapUpModalProps) {
  const [step, setStep] = useState<WrapUpStep>(initialStep);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSendFeedback = async () => {
    if (rating === 0) {
      setFeedbackError('Please select a rating');
      toast.error('Please select a rating');
      return;
    }
    setSubmitting(true);
    setFeedbackError(null);
    try {
      await onSubmitFeedback(rating, message);
      toast.success('Thank you for your feedback!');
      setStep('share');
    } catch (error) {
      console.error('Wrap-up feedback error:', error);
      const text = error instanceof Error ? error.message : 'Failed to submit feedback';
      setFeedbackError(text);
      toast.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="show-wrap-up-modal">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="That's a wrap"
      >
        <div className="flex justify-end p-2">
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'thanks' && (
          <div className="px-6 pb-6 text-center space-y-4">
            <PartyPopper className="w-12 h-12 mx-auto text-primary" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">That&rsquo;s a wrap! 🎉</h2>
            <p className="text-gray-600 dark:text-gray-400">Thanks for making the playlist tonight at {eventName}.</p>
            <button
              type="button"
              onClick={() => setStep(showFeedback ? 'feedback' : 'share')}
              className="btn btn-primary w-full"
            >
              Next
            </button>
          </div>
        )}

        {step === 'feedback' && (
          <div className="px-6 pb-6 space-y-5">
            <div className="text-center">
              <Heart className="w-10 h-10 mx-auto text-primary mb-2" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How was your experience?</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Let the DJ know how they did tonight</p>
            </div>
            <div className="space-y-2">
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => { setRating(star); setFeedbackError(null); }}
                    className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary rounded"
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                  >
                    <Star
                      className={`w-9 h-9 transition-colors ${
                        star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">{RATING_LABELS[rating]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Comments (optional)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell the DJ what you loved or what could be improved..."
                className="input min-h-[90px] resize-none"
                maxLength={500}
              />
            </div>
            {feedbackError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{feedbackError}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep('share')}
                disabled={submitting}
                className="btn btn-secondary flex-1"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSendFeedback}
                disabled={submitting}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send
              </button>
            </div>
          </div>
        )}

        {step === 'share' && (
          <div className="px-6 pb-6 text-center space-y-4">
            <Share2 className="w-10 h-10 mx-auto text-primary" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Spread the word</h2>
            <button type="button" onClick={onShare} className="btn btn-primary w-full flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" />
              Share this event
            </button>
            <a
              href={nextEventContactPath(eventId, 'wrap-up')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 underline font-medium"
            >
              VoteBeats at your next event?
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button type="button" onClick={onClose} className="btn btn-secondary w-full">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
